import {
  type BusinessDayDetailResource,
  type BusinessDayEventResource,
  type DayCloseResource,
  type DayReopenResource,
  OutletBusinessDaysController_lookup_v1,
  type OutletResource,
  OutletsController_list_v1,
  ReconciliationController_read_v1,
  type ReconciliationResource,
  type RungUpLineResource,
  type TillPayoutRecordedResource,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconLock, IconLockOpen, IconReceipt } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound } from '@/api/failure';
import { useSession } from '@/auth/session';
import { systemClock } from '@/lib/clock';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { LoadFailed, Screen, ScreenFact, ScreenTable, TableNotice } from '../back-office/Screen';
import { currentBusinessDate } from '../reporting/reporting-period';
import {
  formatBusinessDate,
  fromCalendarDate,
  isCalendarDate,
  toCalendarDate,
} from '../tickets/business-date';
import { CloseDayDialog } from './CloseDayDialog';
import { countedStockOf, labelForRungUpMode, varianceOf } from './day-close-form';
import { RecordPayoutDialog } from './RecordPayoutDialog';
import { ReopenDayDialog } from './ReopenDayDialog';

/**
 * Cashing up: one outlet's trading day, the three-way reconciliation somebody
 * closing is looking at, and the four acts that change it.
 *
 * - `GET  …/business-days/{date}` — the day, and **every** close and reopen it
 *   has ever had.
 * - `GET  …/business-days/{date}/reconciliation` — rung up, counted stock, cash.
 * - `POST …/business-days/{date}/close` — shut it on a counted figure.
 * - `POST …/business-days/{date}/reopen` — an owner puts it back.
 * - `POST /v1/outlets/{outletId}/till-payouts` — cash out of the drawer.
 *
 * ## The two acts on this screen do not carry the same guard
 *
 * **Closing is `OWNER` or `MANAGER`. Reopening is `OWNER` alone** — a manager may
 * not reopen whatever outlet they are assigned to, because reopening undoes a
 * declaration another person signed off (CL-603). The route declaration mirrors
 * the *read*, which both roles hold; the reopen control is shown to an owner
 * only, and the sentence a manager gets in its place says *owner*, because two
 * refusal messages in this repo once told people to fetch a manager for an act
 * only an owner may perform.
 *
 * The boundary on closing is the **role and not the outlet assignment**. FR-642c,
 * which confined a manager to their own outlets, was withdrawn on 16 Aug 2026 as
 * unimplementable against the identity model, so the outlet picker offers every
 * outlet in reach to both roles. A guard narrower than the API's would hide a
 * screen from somebody the server would have served, and nothing would say so.
 *
 * ## Three things this screen is careful not to re-derive
 *
 * 1. **A variance never refuses a close** (CL-601, FR-619a). There is no
 *    threshold to check and no confirmation to invent; `CloseDayDialog` says
 *    this at length.
 * 2. **`state` is a question about now.** `OPEN | CLOSED` is "whether the day is
 *    open **now**", explicitly not a one-way transition since an owner may
 *    reopen (FR-613f) — so it is re-read after every act rather than cached as
 *    terminal, and every write on this screen is followed by a fresh read.
 * 3. **Nulls are states, not gaps.** `countedPaise`, `variancePaise`, `countedAt`
 *    and `countedBy` are null for exactly as long as the day is open, including a
 *    day that has been reopened. `day-close-form.ts` turns them into answers so
 *    nothing here can render a null as `₹0.00`.
 *
 * ## Why the day is re-read rather than patched from the answer
 *
 * `DayCloseResource` carries the day and the reconciliation as they now stand,
 * and it would be tempting to write both straight into state. It is not enough:
 * `DayCloseResource.day` is a `BusinessDayResource` and carries **no `events`**,
 * so a screen patched from it would show a freshly closed day whose history had
 * lost the act that just happened. The reopen answers a `BusinessDayDetailResource`
 * and the payout answers neither. One re-read after every act is one rule instead
 * of three, and it is also what picks up anything another cash-up did meanwhile.
 */

/** What the field is showing. `idle` is "no outlet chosen", which is not a load. */
type SheetState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly day: BusinessDayDetailResource;
      readonly reconciliation: ReconciliationResource;
    }
  /** The outlet did not trade on that date — or is unknown, or out of reach. */
  | { readonly kind: 'no-day' }
  | { readonly kind: 'failed'; readonly error: unknown };

/** What just happened, said above the sheet it changed. */
type Notice =
  | { readonly kind: 'closed'; readonly closed: DayCloseResource }
  | { readonly kind: 'reopened'; readonly reopened: DayReopenResource }
  | { readonly kind: 'payout'; readonly payout: TillPayoutRecordedResource };

const NO_OUTLET = '';

export function DayClose() {
  const client = useApiClient();
  const { session } = useSession();

  /**
   * Whether to offer the reopen control at all.
   *
   * Courtesy and not authorisation — `role` is restored from `sessionStorage`
   * and is editable by whoever is at the keyboard. Somebody who puts the button
   * back meets `AUTH_FORBIDDEN` from the server, which the dialog words like any
   * other refusal.
   */
  const mayReopen = session?.user.role === 'OWNER';

  /**
   * `UsersController` carries `@RequireRole('OWNER')` on every operation, and the
   * staff directory is the only thing that turns an `actorId` into a name.
   *
   * **This is a gap between the API and the screen, not a decision taken here.**
   * `BusinessDayEventResource` and `TillPayoutResource` both publish `actorId`
   * and no name, while `closedBy` beside them publishes a `DayCloseActorResource`
   * carrying one — so a manager can read who performed the *latest* close and
   * nobody else, on a screen whose whole subject is who did what to a day's cash.
   * The same gap Sales and Tickets report. Named in the PR body; a UUID rendered
   * where a name belongs typechecks perfectly, so it is not cast around here.
   */
  const mayListPeople = session?.user.role === 'OWNER';

  const [outletId, setOutletId] = useState<string>(NO_OUTLET);

  /**
   * The trading day, opening on the park's current one.
   *
   * `currentBusinessDate` takes the injected clock and formats in Asia/Kolkata
   * (R-006) rather than reading the device's local getters — the back office is a
   * browser on somebody's desk and neither it nor a supplier-configured terminal
   * is guaranteed to be set to IST. Somebody cashing up is almost always cashing
   * up today, and the picker moves off it in one click.
   *
   * **Imported from `routes/reporting/` deliberately**, and it is the lesser of
   * two faults, exactly as `expense-form.ts` argues for its own cross-epic
   * import: the alternative is a second copy of a zone-fixed formatter, and
   * `lib/field-schemas.ts` exists because two copies of one rule drifted with
   * nothing in `pnpm validate` able to notice. It is the second consumer
   * arriving, which is the moment the conventions say to promote it to `lib/` —
   * that move is Reporting's to make and is named in the PR body rather than
   * taken here.
   */
  const [businessDate, setBusinessDate] = useState<string>(() => currentBusinessDate(systemClock));

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const [people, setPeople] = useState<readonly UserResource[]>([]);

  const [state, setState] = useState<SheetState>({ kind: 'idle' });
  const [notice, setNotice] = useState<Notice | null>(null);

  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [payingOut, setPayingOut] = useState(false);

  // Bumping this re-reads the day. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => {
    setReloads((count) => count + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones are
    // excluded by the API's own default, and correctly — a day at a retired
    // outlet answers 404 indistinguishably from one that never existed.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty, and the screen says so where the
        // picker is rather than replacing the sheet with an error about a
        // control.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (!mayListPeople) return;

    const controller = new AbortController();
    let live = true;

    collectPages<UserResource>((offset, limit) =>
      client.request(UsersController_list_v1, {
        query: { offset, limit, includeDeactivated: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setPeople(all);
      })
      .catch(() => {
        // Names are a courtesy on this screen and their absence is already
        // handled below. A failed directory read must not take the
        // reconciliation down with it.
        if (live) setPeople([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayListPeople]);

  useEffect(() => {
    // Nothing to ask for. A malformed date is refused `400 VALIDATION_FAILED`
    // for something the screen can see — 31 February is not a date, and the
    // server refuses one rather than reading it as 3 March.
    if (outletId === NO_OUTLET || !isCalendarDate(businessDate)) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    // Both reads together. They answer the same three refusals for the same
    // three reasons, and a screen that showed one without the other would be a
    // reconciliation with no idea whether its day is open.
    Promise.all([
      client.request(OutletBusinessDaysController_lookup_v1, {
        path: { outletId, businessDate },
        signal: controller.signal,
      }),
      client.request(ReconciliationController_read_v1, {
        path: { outletId, businessDate },
        signal: controller.signal,
      }),
    ])
      .then(([day, reconciliation]) => {
        if (live) setState({ kind: 'loaded', day, reconciliation });
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (!live || (error instanceof DOMException && error.name === 'AbortError')) return;

        // Not a failure but an answer: this outlet did not trade that day. The
        // API folds three cases into it deliberately (FR-640) and the notice
        // says so rather than claiming to know which.
        setState(isNotFound(error) ? { kind: 'no-day' } : { kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, businessDate, reloads]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  // Keyed by `actorId` and not by `id`. A day's events name the **actor** a
  // person acts as in history, which is a separate column on `UserResource`.
  const personNames = useMemo(
    () => new Map(people.map((person) => [person.actorId, person.displayName])),
    [people],
  );

  const chosenOutlet = outlets.find((outlet) => outlet.id === outletId);
  const outletName = chosenOutlet?.name ?? 'this outlet';

  const day = state.kind === 'loaded' ? state.day : null;
  const reconciliation = state.kind === 'loaded' ? state.reconciliation : null;

  /** A day's act, once one has loaded. Exactly one, and it is the screen's accent. */
  const action =
    day === null ? undefined : day.state === 'OPEN' ? (
      <Button
        onClick={() => {
          setClosing(true);
        }}
      >
        <IconLock data-icon="inline-start" />
        Close the day
      </Button>
    ) : mayReopen ? (
      // `outline` and not `destructive`. Red is for irreversible acts and for
      // refusals, and a reopen is neither — it appends, and closing again undoes
      // it. It is also not the screen's accent: on a closed day there is no
      // primary act, because the day is already where it should be.
      <Button
        variant="outline"
        onClick={() => {
          setReopening(true);
        }}
      >
        <IconLockOpen data-icon="inline-start" />
        Reopen the day
      </Button>
    ) : undefined;

  const changeSubject = (apply: () => void) => {
    apply();

    // The notice is about the day it was raised on. Carried across a change of
    // outlet or date it would read as a statement about the sheet now on screen.
    setNotice(null);
  };

  return (
    <Screen
      title="Day close"
      description="One outlet's trading day: what was rung up, what was counted, and the difference between them. Closing records what a person counted in the drawer — never a difference, and no figure stops it. An owner can reopen a closed day, and nothing is erased when they do."
      action={action}
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="day-close-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeSubject(() => {
                  // Base UI types the value as nullable because a select may be
                  // cleared. `''` is what this screen calls "nothing chosen".
                  setOutletId(next ?? NO_OUTLET);
                });
              }}
              disabled={outlets.length === 0}
            >
              <SelectTrigger
                id="day-close-outlet"
                aria-label="Outlet"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue placeholder="Choose an outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {outletsAnswered && outlets.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No outlet is in reach from this account. Somebody who staffs the park sets the
                outlets up.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="day-close-date" className="text-sm text-muted-foreground">
              Trading day
            </Label>
            <DayPicker
              id="day-close-date"
              value={businessDate}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeSubject(() => {
                  setBusinessDate(next);
                });
              }}
            />
          </div>

          {day !== null && (
            <>
              <ScreenFact label="State">
                <Badge variant="outline">{day.state === 'OPEN' ? 'Open' : 'Closed'}</Badge>
                <br />
                <span className="text-muted-foreground">
                  {day.state === 'OPEN'
                    ? 'As things stand now. Closing is not one-way — an owner can put a closed day back.'
                    : 'As things stand now. A closed day is not closed for good; an owner can reopen it.'}
                </span>
              </ScreenFact>

              <ScreenFact label="Opened">
                <span className="numeric">{formatInstant(day.openedAt)}</span>
                <br />
                <span className="text-muted-foreground">
                  When the first record of the day arrived. A day is never opened by reading it.
                </span>
              </ScreenFact>

              {day.closedAt !== null && (
                <ScreenFact label="Closed">
                  <span className="numeric">{formatInstant(day.closedAt)}</span>
                  {day.closedBy !== null && <> by {day.closedBy.name}</>}
                </ScreenFact>
              )}

              {day.reopenCount > 0 && (
                <ScreenFact label="Reopened">
                  {day.reopenCount === 1 ? 'Once' : `${String(day.reopenCount)} times`}
                  <br />
                  <span className="text-muted-foreground">
                    The closes it has had are all in its history below, each with the figure it was
                    closed on. Nothing is erased by a reopen.
                  </span>
                </ScreenFact>
              )}

              {day.state === 'CLOSED' && !mayReopen && (
                <ScreenFact label="Reopening">
                  An owner&rsquo;s act.
                  <br />
                  <span className="text-muted-foreground">
                    Reopening undoes a close somebody signed off, so a manager cannot do it whatever
                    outlet they are assigned to.
                  </span>
                </ScreenFact>
              )}
            </>
          )}
        </>
      }
    >
      {notice !== null && <ActNotice notice={notice} businessDate={businessDate} />}

      {state.kind === 'idle' && (
        <Panel title="Choose an outlet">
          <p className="text-sm text-muted-foreground">
            A trading day belongs to one outlet — there is one per outlet per date, and each is
            counted against its own drawer. Choose the one being cashed up, and the day beside it.
          </p>
        </Panel>
      )}

      {state.kind === 'loading' && <SheetSkeleton />}

      {state.kind === 'no-day' && (
        <Panel title={`Nothing traded on ${formatBusinessDate(businessDate)}`}>
          <p className="text-sm text-muted-foreground">
            {outletName} has no trading day on this date. A day opens when its first record arrives,
            so a date nobody sold anything on has no day to close and none to reconcile. The same
            answer covers an outlet that is unknown or outside this account&rsquo;s reach — the API
            does not tell the three apart, deliberately.
          </p>
        </Panel>
      )}

      {state.kind === 'failed' && (
        <Panel title="This day could not be read">
          <LoadFailed
            error={state.error}
            onRetry={reload}
            title="This trading day could not be read"
          />
        </Panel>
      )}

      {day !== null && reconciliation !== null && (
        <>
          <CashPanel
            reconciliation={reconciliation}
            personNames={personNames}
            onRecordPayout={() => {
              setPayingOut(true);
            }}
          />

          <RungUpPanel reconciliation={reconciliation} />

          <CountedStockPanel reconciliation={reconciliation} />

          <HistoryPanel day={day} personNames={personNames} mayListPeople={mayListPeople} />
        </>
      )}

      {chosenOutlet !== undefined && day !== null && day.state === 'OPEN' && (
        <CloseDayDialog
          open={closing}
          onOpenChange={setClosing}
          outletId={chosenOutlet.id}
          outletName={chosenOutlet.name}
          businessDate={day.businessDate}
          onClosed={(closed) => {
            setClosing(false);
            setNotice({ kind: 'closed', closed });
            reload();
          }}
        />
      )}

      {chosenOutlet !== undefined && day !== null && day.state === 'CLOSED' && mayReopen && (
        <ReopenDayDialog
          open={reopening}
          onOpenChange={setReopening}
          outletId={chosenOutlet.id}
          outletName={chosenOutlet.name}
          businessDate={day.businessDate}
          onReopened={(reopened) => {
            setReopening(false);
            setNotice({ kind: 'reopened', reopened });
            reload();
          }}
        />
      )}

      {chosenOutlet !== undefined && (
        <RecordPayoutDialog
          open={payingOut}
          onOpenChange={setPayingOut}
          outletId={chosenOutlet.id}
          outletName={chosenOutlet.name}
          onRecorded={(payout) => {
            setPayingOut(false);
            setNotice({ kind: 'payout', payout });
            reload();
          }}
        />
      )}
    </Screen>
  );
}

/* -------------------------------------------------------------------------- */
/* The sheet                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One band of the sheet.
 *
 * **A ruled region, not a card.** The panel is the surface now, so a bordered,
 * rounded box on it is a card inside a card; what a band needs is a heading and
 * a hairline. The gutter goes with the border, so the cash column's amounts
 * right-align to the field's own edge — the same edge the head and the toolbar
 * use, and the one `docs/ui-conventions.md` asks a column of money to be read
 * down against.
 *
 * The heading is body size at weight 500. `docs/design-language.md` allows one
 * size increase per screen and spends it on the page title; four panels each
 * shouting would leave the reader with no idea which one to read first.
 *
 * `aria-labelledby` on the section is what makes each band a **named region**
 * rather than an anonymous `<section>`. Four cards of figures on one screen is
 * exactly the case where somebody navigating by landmark needs to be able to
 * jump to Cash without reading past the rest of it.
 */
function Panel({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="pt-2">
      <header className="flex flex-wrap items-start gap-3 border-b border-border pb-3">
        <div className="min-w-0 flex-1">
          <h2 id={headingId} className="font-medium text-foreground">
            {title}
          </h2>
          {description !== undefined && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {action !== undefined && <div className="ml-auto shrink-0">{action}</div>}
      </header>
      <div className="pt-5">{children}</div>
    </section>
  );
}

/**
 * One line of the cash column: a label, an amount, and an optional word after it.
 *
 * Amounts right-align and carry `numeric`, per `docs/ui-conventions.md` — Geist's
 * default figures are proportional, and cash-up is precisely the task of
 * comparing down a column.
 */
function Figure({
  label,
  children,
  strong = false,
}: {
  label: string;
  children: ReactNode;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-2 ${strong ? 'border-t border-border pt-3' : ''}`}
    >
      <dt className={strong ? 'text-foreground' : 'text-muted-foreground'}>{label}</dt>
      <dd className={`text-right ${strong ? 'font-medium text-foreground' : 'text-foreground'}`}>
        {children}
      </dd>
    </div>
  );
}

const Money = ({ paise }: { paise: number }) => (
  <span className="font-mono numeric">{formatPaise(paise)}</span>
);

/**
 * The drawer: what should be in it, what was found in it, and the difference.
 *
 * The subtractions are rendered as negatives rather than as positives beside a
 * minus label, because this is a column somebody adds up by eye and a row that
 * reads `₹800.00` under one that reads `₹11,800.00` invites the wrong sum. The
 * arithmetic itself is **the server's** — `expectedPaise` is derived on every
 * read and stored nowhere, and nothing here recomputes it (FR-616, R-608).
 */
function CashPanel({
  reconciliation,
  personNames,
  onRecordPayout,
}: {
  reconciliation: ReconciliationResource;
  personNames: ReadonlyMap<string, string>;
  onRecordPayout: () => void;
}) {
  const { cash } = reconciliation;
  const variance = varianceOf(cash.variancePaise);

  return (
    <Panel
      title="Cash"
      description="Cash taken, cash handed back, cash paid out, and what that leaves. Non-cash takings are in the day's rung-up figures below and never touch this column."
      action={
        <Button variant="outline" onClick={onRecordPayout}>
          <IconReceipt data-icon="inline-start" />
          Record a payout
        </Button>
      }
    >
      <dl className="text-sm">
        <Figure label="Taken in cash">
          <Money paise={cash.takingsPaise} />
        </Figure>

        <Figure label="Handed back in cash">
          {/* Negated for display only. The API publishes a positive magnitude
              and the direction is in the name of the field. */}
          <Money paise={-cash.refundsPaise} />
        </Figure>

        <Figure label="Paid out of the drawer">
          <Money paise={-cash.payoutsPaise} />
        </Figure>

        <Figure label="Expected in the drawer" strong>
          <Money paise={cash.expectedPaise} />
        </Figure>

        <Figure label="Counted at the close" strong>
          {cash.countedPaise === null ? (
            <span className="font-normal text-muted-foreground">Not counted — the day is open</span>
          ) : (
            <Money paise={cash.countedPaise} />
          )}
        </Figure>

        <Figure label="Difference" strong>
          {variance.kind === 'not-counted' ? (
            <span className="font-normal text-muted-foreground">
              There is none until the drawer is counted
            </span>
          ) : variance.kind === 'exact' ? (
            <>
              <Money paise={0} />{' '}
              <span className="font-normal text-muted-foreground">— it came out exact</span>
            </>
          ) : (
            <>
              <Money paise={variance.kind === 'short' ? -variance.byPaise : variance.byPaise} />{' '}
              <span className="font-normal text-muted-foreground">
                {variance.kind === 'short' ? 'short' : 'over'}
              </span>
            </>
          )}
        </Figure>
      </dl>

      {cash.countedAt !== null && (
        <p className="mt-2 text-sm text-muted-foreground">
          Counted <span className="numeric">{formatInstant(cash.countedAt)}</span>
          {cash.countedBy !== null && <> by {cash.countedBy.name}</>}. The figure is kept exactly as
          it was submitted and is never adjusted towards what was expected. A difference is a fact
          about the day rather than a task somebody clears.
        </p>
      )}

      <h3 className="mt-6 mb-2 font-medium text-foreground">Payouts</h3>

      {cash.payouts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No cash left the drawer for anything other than a refund. A payout is the only other way
          it can, and it always carries a reason and the person who authorised it.
        </p>
      ) : (
        <ScreenTable label="Cash paid out of the drawer">
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>What it was for</TableHead>
              <TableHead>Authorised by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cash.payouts.map((payout) => (
              <TableRow key={payout.id}>
                <TableCell className="text-right">
                  <Money paise={payout.amountPaise} />
                </TableCell>
                {/* Free text a person wrote, returned as data and rendered as
                    data. `whitespace-normal` because the registry's cell is
                    `nowrap`, which sizes to the full sentence and drags the
                    table past the viewport. */}
                <TableCell className="whitespace-normal">{payout.reason}</TableCell>
                <TableCell>{personNames.get(payout.actorId) ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScreenTable>
      )}
    </Panel>
  );
}

/**
 * What the day took and what it handed back, by payment mode.
 *
 * **Published separately rather than netted off**, and shown that way: a day that
 * took ₹10,000 and returned ₹2,000 is not a day that took ₹8,000. A mode with no
 * movement is absent rather than zero, so an empty list is a real answer.
 *
 * No totals row. `docs/ui-conventions.md` is explicit that the client does not
 * compute totals, and the API publishes none for these two — the only total on
 * this screen that means anything is `expectedPaise`, which is the cash column's
 * and is the server's own.
 */
function RungUpPanel({ reconciliation }: { reconciliation: ReconciliationResource }) {
  const { rungUp } = reconciliation;

  return (
    <Panel
      title="Rung up"
      description="Every movement in the day's ledger, grouped by how it was paid. A till payout does not appear here — it takes cash out of the drawer without changing what the day sold."
    >
      <div className="grid gap-6 md:grid-cols-2">
        <RungUpLines label="Taken" lines={rungUp.takings} empty="Nothing was taken on this day." />
        <RungUpLines
          label="Handed back"
          lines={rungUp.returned}
          empty="Nothing was handed back — no refunds and no voided tickets."
        />
      </div>
    </Panel>
  );
}

function RungUpLines({
  label,
  lines,
  empty,
}: {
  label: string;
  lines: readonly RungUpLineResource[];
  empty: string;
}) {
  return (
    <div>
      <h3 className="mb-2 font-medium text-foreground">{label}</h3>
      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <dl className="text-sm">
          {lines.map((line) => (
            <Figure
              key={line.paymentMode ?? 'unknown'}
              label={labelForRungUpMode(line.paymentMode)}
            >
              <Money paise={line.amountPaise} />
            </Figure>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * The stocktake, which has **three** answers and not two.
 *
 * The trap is `NOT_APPLICABLE`: the gate carries no stock, and rendering that as
 * `0 / 0` reads as a reconciled count when nothing was counted at all (FR-632).
 * `countedStockOf` makes the distinction a union so there is no zero here to
 * render by accident.
 */
function CountedStockPanel({ reconciliation }: { reconciliation: ReconciliationResource }) {
  const counted = countedStockOf(reconciliation.countedStock);

  return (
    <Panel
      title="Counted stock"
      description="Whether anybody counted the shelves against the books on this day. It is the third leg of the reconciliation and it is not about cash."
    >
      {counted.kind === 'not-applicable' && (
        <p className="text-sm text-muted-foreground">
          This outlet carries no stock, so there was nothing to count. That is not a count of zero
          and not a stocktake anybody has missed.
        </p>
      )}

      {counted.kind === 'not-counted' && (
        <p className="text-sm text-muted-foreground">
          This outlet carries stock and nobody counted it on this day. Closing does not require one
          — a stocktake is its own act, and the day can be closed without it.
        </p>
      )}

      {counted.kind === 'counted' && (
        <dl className="text-sm">
          <Figure label="Items counted">
            <span className="font-mono numeric">{String(counted.stocktakeCount)}</span>
          </Figure>
          <Figure label="Items whose count moved the books">
            {/* `itemsWithVariance` nets per item (blossom-rest-api#454). An item miscounted +3
                and then counted back publishes 0 — true of the books, which did
                not move, but "Disagreed with the books: 0" read as "nothing was
                ever wrong here" when three counting acts happened and two were
                wrong. This label states what the figure measures and claims
                nothing about whether anything was ever wrong. The individual
                acts are readable at GET /v1/outlets/{outletId}/stock/movements,
                not here. */}
            <span className="font-mono numeric">{String(counted.itemsWithVariance)}</span>
          </Figure>
        </dl>
      )}
    </Panel>
  );
}

/**
 * Every close and every reopen this day has had, oldest first.
 *
 * **A reopen appends; it does not erase** (FR-613c). A day closed, reopened and
 * re-closed keeps all three acts and both counted figures, each attributed and
 * each stamped by the server clock — which is the only thing that orders them,
 * because both acts happen at a keyboard talking to the server. So this is a
 * sequence of acts rather than a restatement of the current state, which the
 * rail already carries.
 */
function HistoryPanel({
  day,
  personNames,
  mayListPeople,
}: {
  day: BusinessDayDetailResource;
  personNames: ReadonlyMap<string, string>;
  mayListPeople: boolean;
}) {
  return (
    <Panel
      title="What has been done to this day"
      description="Closes and reopens, in the order they happened. Nothing here is ever removed — a reopen adds a line, it does not take one away."
    >
      <ScreenTable label="Closes and reopens on this trading day">
        <TableHeader>
          <TableRow>
            <TableHead>Act</TableHead>
            <TableHead>What was recorded</TableHead>
            <TableHead>Who</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        {day.events.length === 0 ? (
          <TableNotice columns={4}>
            <p className="font-medium text-foreground">This day has never been closed</p>
            <p className="text-sm text-muted-foreground">
              It opened when its first record arrived and it is still taking money. Closing it
              records what somebody counted in the drawer.
            </p>
          </TableNotice>
        ) : (
          <TableBody>
            {day.events.map((event) => (
              <TableRow key={`${event.kind}-${event.recordedAt}`}>
                <TableCell>{event.kind === 'CLOSE' ? 'Closed' : 'Reopened'}</TableCell>
                <TableCell className="whitespace-normal">
                  <EventDetail event={event} />
                </TableCell>
                <TableCell>{personNames.get(event.actorId) ?? '—'}</TableCell>
                <TableCell className="numeric">{formatInstant(event.recordedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </ScreenTable>

      {!mayListPeople && day.events.length > 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          Naming the person who acted needs the staff directory, which is the owner&rsquo;s. The
          latest close is named in the rail because the day itself carries that one.
        </p>
      )}
    </Panel>
  );
}

/**
 * What one act recorded — a counted figure on a close, a written reason on a
 * reopen, and never both.
 *
 * The API pins that with a database CHECK in both directions: `countedCashPaise`
 * is null on a REOPEN because a reopen does not recount the drawer, the next
 * close does (FR-619c). The nulls are read rather than asserted away, so a
 * contract that moved shows as a missing sentence instead of `₹NaN`.
 */
function EventDetail({ event }: { event: BusinessDayEventResource }) {
  if (event.kind === 'CLOSE') {
    return event.countedCashPaise === null ? (
      <span className="text-muted-foreground">No counted figure was recorded.</span>
    ) : (
      <>
        Counted <Money paise={event.countedCashPaise} /> in the drawer.
      </>
    );
  }

  return event.reason === null ? (
    <span className="text-muted-foreground">No reason was recorded.</span>
  ) : (
    // Free text a person wrote. Returned as data, rendered as data.
    <>{event.reason}</>
  );
}

/**
 * What just happened, above the sheet it changed.
 *
 * `role="status"` and no destructive colour on all three: closing a day,
 * reopening one and recording a payout are the screen working, not warnings. The
 * payout notice names the day the **server** put it in, because the route takes
 * no date — and says so plainly when that is not the day on screen, rather than
 * leaving somebody looking at a reconciliation their payout is not in.
 */
function ActNotice({ notice, businessDate }: { notice: Notice; businessDate: string }) {
  if (notice.kind === 'closed') {
    const counted = notice.closed.reconciliation.cash.countedPaise;

    return (
      <Alert role="status">
        <IconCircleCheck />
        <AlertTitle>The day is closed</AlertTitle>
        <AlertDescription>
          <span>
            {counted === null ? (
              'The counted figure is recorded against the day.'
            ) : (
              <>
                Closed on a counted <Money paise={counted} />.
              </>
            )}{' '}
            The difference against the books is worked out and kept with the day; it does not have
            to be cleared by anybody. If the count was wrong, an owner can reopen the day and the
            figure below stays in its history either way.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  if (notice.kind === 'reopened') {
    return (
      <Alert role="status">
        <IconLockOpen />
        <AlertTitle>The day is open again</AlertTitle>
        <AlertDescription>
          <span>
            The close that was set aside is still in the day&rsquo;s history with the figure it was
            closed on, and the reason for reopening is beside it. The day now behaves exactly as one
            that was never closed — every write that was refused against it is accepted again, and
            it can be closed afresh.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  const { payout } = notice;
  const elsewhere = payout.businessDate !== businessDate;

  return (
    <Alert role="status">
      <IconReceipt />
      <AlertTitle>Payout recorded</AlertTitle>
      <AlertDescription>
        <span>
          <Money paise={payout.amountPaise} /> out of the drawer, against the trading day{' '}
          <span className="numeric">{formatBusinessDate(payout.businessDate)}</span>.{' '}
          {elsewhere
            ? 'That is not the day on screen. A payout always lands in the outlet’s current trading day, which the server decides, so cash cannot be moved between days after the fact — switch to that date to see it in the figures.'
            : 'It reduces the cash expected in the drawer and leaves what the day sold untouched.'}{' '}
          A payout is never edited — a mistake is put right by a second one saying so.
        </span>
      </AlertDescription>
    </Alert>
  );
}

/**
 * The sheet's shape while it is in flight.
 *
 * A skeleton and not a spinner, and one whose bands are the height the panels
 * will be, so the page does not jump when the figures land — a layout shift
 * under a pointer is how somebody clicks the row below the one they aimed at.
 * `aria-hidden` because there is nothing here to read.
 */
function SheetSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-4">
      {[220, 180, 120, 160].map((height) => (
        <div
          key={height}
          className="animate-pulse rounded-2xl bg-muted"
          style={{ height: `${String(height)}px` }}
        />
      ))}
    </div>
  );
}
